"use client"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom" // Corrected import for React Router
import { Search, Grid, List, Calendar, Clock } from "lucide-react"
import "./news.css"
import placeholder from "../../images/placeholder.png"

const News = () => {
  const [newsItems, setNewsItems] = useState([])
  const [filteredNews, setFilteredNews] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [viewMode, setViewMode] = useState("grid")
  const [sortBy, setSortBy] = useState("newest")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const navigate = useNavigate()
  const API_BASE_URL = "https://imperia-backend.onrender.com/api/news"
  const BEARER_TOKEN = "YWRtaW5AZXhhbXBsZS5jb206c3VwZXJzZWN1cmU="

  const [categories, setCategories] = useState([{ id: "all", name: "All News" }])

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${BEARER_TOKEN}`,
    "Content-Type": "application/json",
  })

  const generateNewsData = (news) => ({
    id: news.newsId || news.id,
    headline: news.headline || news.title,
    description: news.description || news.content,
    date: news.publishedDate || news.date || news.createdAt,
    category: news.category,
    imageUrl: news.photo || news.image || placeholder,
    author: news.author,
    url: news.url,
    source: news.source,
  })

  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true)
      setError(null)

      try {
        const response = await fetch(API_BASE_URL, {
          method: "GET",
          headers: getAuthHeaders(),
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()

        if (result.success && result.data && Array.isArray(result.data)) {
          const newsData = result.data.map(generateNewsData)
          setNewsItems(newsData)
          setFilteredNews(newsData)

          // Extract unique categories from API data only
          const uniqueCategories = [...new Set(newsData.map((news) => news.category).filter(Boolean))]
          const categoryOptions = [
            { id: "all", name: "All News" },
            ...uniqueCategories.map((cat) => ({
              id: cat,
              name: cat.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
            })),
          ]
          setCategories(categoryOptions)
        } else {
          throw new Error("Invalid API response format")
        }
      } catch (error) {
        console.error("Error fetching news:", error)
        setError(error.message)
        // Don't set fallback data - keep arrays empty if API fails
        setNewsItems([])
        setFilteredNews([])
      } finally {
        setLoading(false)
      }
    }

    fetchNews()
  }, [])

  useEffect(() => {
    let filtered = newsItems

    if (selectedCategory !== "all") {
      filtered = newsItems.filter((news) => news.category === selectedCategory)
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (news) =>
          (news.headline && news.headline.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (news.description && news.description.toLowerCase().includes(searchTerm.toLowerCase())),
      )
    }
    console.log(newsItems)
    filtered.sort((a, b) => {
      const dateA = new Date(a.date).getTime()
      const dateB = new Date(b.date).getTime()
      switch (sortBy) {
        case "oldest":
          return dateA - dateB
        case "newest":
        default:
          return dateB - dateA
      }
    })

    setFilteredNews(filtered)
  }, [searchTerm, selectedCategory, newsItems, sortBy])

  const formatDate = (dateString) => {
    if (!dateString) return "No date"
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const formatTime = (dateString) => {
    if (!dateString) return "No time"
    const date = new Date(dateString)
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const handleNewsClick = (news) => {
    if (news.url) {
      // Open external news article in new tab
      window.open(news.url, "_blank", "noopener,noreferrer")
    } else {
      // Navigate to internal news detail page
      navigate(`/news/${news.id}`) // Corrected navigation for React Router
    }
  }

  const refreshNews = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(API_BASE_URL, {
        method: "GET",
        headers: getAuthHeaders(),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()

      if (result.success && result.data && Array.isArray(result.data)) {
        const newsData = result.data.map(generateNewsData)
        setNewsItems(newsData)
        setFilteredNews(newsData)

        // Update categories from fresh data
        const uniqueCategories = [...new Set(newsData.map((news) => news.category).filter(Boolean))]
        const categoryOptions = [
          { id: "all", name: "All News" },
          ...uniqueCategories.map((cat) => ({
            id: cat,
            name: cat.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
          })),
        ]
        setCategories(categoryOptions)
      } else {
        throw new Error("Invalid API response format")
      }
    } catch (error) {
      console.error("Error refreshing news:", error)
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const NewsCard = ({ news }) => (
    <div
      className={`news-card ${viewMode === "grid" ? "grid-card" : "list-card"}`}
      onClick={() => handleNewsClick(news)}
    >
      <div className="news-image-container">
        {news.imageUrl ? (
          <img
            src={news.imageUrl || "/placeholder.svg"}
            alt={news.headline || "News image"}
            className="news-image"
            onError={(e) => {
              e.target.style.display = "none"
            }}
          />
        ) : (
          <div className="news-image-placeholder">
            <span>No Image</span>
          </div>
        )}
        {news.category && (
          <span className="category-tag">{news.category.charAt(0).toUpperCase() + news.category.slice(1)}</span>
        )}
      </div>
      <div className="news-content">
        <h3 className="headline">{news.headline || "No headline available"}</h3>
        <p className="description">
          {news.description
            ? news.description.length > 120
              ? `${news.description.substring(0, 120)}...`
              : news.description
            : "No description available"}
        </p>
        <div className="meta-info">
          <div className="date-time">
            <Calendar size={14} />
            <span>{formatDate(news.date)}</span>
            <Clock size={14} />
            <span>{formatTime(news.date)}</span>
          </div>
          <div className="source-author">
            {news.source && <span className="source">{news.source}</span>}
            {news.author && <span className="author">By {news.author}</span>}
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="news-page-wrapper">
      {/* Hero Section */}
      <section className="news-hero-section">
        <div className="news-hero-container">
          <div className="news-hero-content">
            <span className="news-hero-subtitle">STAY INFORMED</span>
            <h1 className="news-hero-title">Latest News & Updates</h1>
            <p className="news-hero-description">
              Stay informed with our comprehensive coverage of breaking news, industry developments, and important
              updates from around the world.
            </p>
          </div>
        </div>
      </section>

      {/* Error Banner */}
      {error && (
        <div className="news-error-banner">
          <p>Error loading news: {error}</p>
          <button onClick={refreshNews} className="news-retry-button">
            Retry
          </button>
        </div>
      )}

      <section className="news-section">
        <div className="news-container">
          <div className="news-header">
            <div className="news-count">Showing {filteredNews.length} news items</div>
            <div className="news-filters-controls">
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="news-category-select">
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
              </select>
              <div className="news-view-toggle">
                <button
                  className={`news-view-button ${viewMode === "grid" ? "active" : ""}`}
                  onClick={() => setViewMode("grid")}
                >
                  <Grid size={16} />
                </button>
                <button
                  className={`news-view-button ${viewMode === "list" ? "active" : ""}`}
                  onClick={() => setViewMode("list")}
                >
                  <List size={16} />
                </button>
              </div>
            </div>
          </div>
          {loading ? (
            <div className="news-loading-container">
              <div className="news-loading-spinner"></div>
              <p className="news-loading-text">Loading News...</p>
            </div>
          ) : error && newsItems.length === 0 ? (
            <div className="news-empty-state">
              <h3>Unable to load news</h3>
              <p>There was an error connecting to the news API. Please check your connection and try again.</p>
              <button onClick={refreshNews} className="news-btn-primary">
                Try Again
              </button>
            </div>
          ) : filteredNews.length > 0 ? (
            <div className={`news-grid ${viewMode === "grid" ? "grid-view" : "list-view"}`}>
              {filteredNews.map((news) => (
                <NewsCard key={news.id} news={news} />
              ))}
            </div>
          ) : (
            <div className="news-empty-state">
              <h3>No news found</h3>
              <p>
                {newsItems.length === 0
                  ? "No news articles available in the database."
                  : "Try adjusting your search criteria or browse all news."}
              </p>
              {newsItems.length > 0 && (
                <button
                  onClick={() => {
                    setSearchTerm("")
                    setSelectedCategory("all")
                  }}
                  className="news-btn-primary"
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default News
